import React, { useState } from 'react';
import { Customer } from '../../services/customerService';
import { X, Save } from 'lucide-react';

interface EditCustomerModalProps {
    customer: Customer;
    onClose: () => void;
    onSave: (id: number, data: { fullName: string; phone?: string; email?: string; type: 'NORMAL' | 'MAYORISTA' }) => Promise<void>;
}

export const EditCustomerModal: React.FC<EditCustomerModalProps> = ({ customer, onClose, onSave }) => {
    const [fullName, setFullName] = useState(customer.fullName);
    const [phone, setPhone] = useState(customer.phone || '');
    const [email, setEmail] = useState(customer.email || '');
    const [type, setType] = useState<'NORMAL' | 'MAYORISTA'>(customer.type || 'NORMAL');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            await onSave(customer.id, { fullName, phone, email, type });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">{customer.id === 0 ? 'Nuevo Cliente' : 'Editar Cliente'}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition p-1">
                        <X size={20} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo *</label>
                        <input
                            type="text"
                            required
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                        <input
                            type="text"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-bold text-gray-900 mb-1">Tipo de Cliente (Tarifa)</label>
                        <select
                            value={type}
                            onChange={e => setType(e.target.value as 'NORMAL' | 'MAYORISTA')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 font-bold"
                        >
                            <option value="NORMAL">NORMAL (Retail)</option>
                            <option value="MAYORISTA">MAYORISTA (Precios Especiales)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Los mayoristas pueden comprar en modo mayoreo sin mínimo de 12 piezas.</p>
                    </div>

                    <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !fullName}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? 'Guardando...' : <><Save size={18} /> Guardar Cambios</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
